/**
 * `useHelpPlace(entry)` — how a screen with steps or tabs tells the help assistant where the person is in it (owner
 * request 2026-10-17; `./place-registry.ts`). Registered while mounted; re-registered only when the step, the steps or
 * the state change (never because `move` is a new function); outside the provider it does nothing.
 */
import { createContext, use, useEffect, useRef, type ReactNode } from "react";

import type { HelpPlaceRegistry, HelpPlaceState } from "./place-registry.js";

export const HelpPlaceContext = createContext<HelpPlaceRegistry | null>(null);

export function HelpPlaceProvider({ registry, children }: { readonly registry: HelpPlaceRegistry; readonly children: ReactNode }): ReactNode {
  return <HelpPlaceContext value={registry}>{children}</HelpPlaceContext>;
}

export interface HelpPlaceInput {
  readonly place: string;
  readonly placeList: readonly string[];
  readonly state?: readonly HelpPlaceState[];
  readonly move: (place: string) => void;
}

export function useHelpPlace(input: HelpPlaceInput | null): void {
  const registry = use(HelpPlaceContext);
  const move = useRef<(place: string) => void>(() => undefined);
  useEffect(() => {
    if (input !== null) move.current = input.move;
  });
  const key = input === null ? null : JSON.stringify([input.place, input.placeList, input.state ?? []]);
  useEffect(() => {
    if (registry === null || key === null) return undefined;
    const [place, placeList, state] = JSON.parse(key) as [string, string[], HelpPlaceState[]];
    return registry.register({ place, placeList, state, move: (next) => move.current(next) });
  }, [registry, key]);
}

/** `useHelpPlace` as an element, for a screen that knows its place only after its early returns (an agent's tabs). */
export function HelpPlace(props: HelpPlaceInput): null {
  useHelpPlace(props);
  return null;
}
